import { computed, onMounted, ref } from 'vue';
import { useNow } from '@/composables/useNow';
import axiosInstance from '@/plugins/axios';
import { useGeoLocation } from '@/composables/useGeoLocation';

function parseHmToMinutes(hm) {
  const [h, m] = String(hm).split(':').map((x) => Number.parseInt(x, 10));
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

function minutesSinceStartOfDay(date) {
  return date.getHours() * 60 + date.getMinutes();
}

function formatSignedHms(ms) {
  const sign = ms < 0 ? '-' : '';
  const abs = Math.abs(ms);
  const totalSeconds = Math.floor(abs / 1000);
  const hh = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
  const mm = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
  const ss = String(totalSeconds % 60).padStart(2, '0');
  return `${sign}${hh}:${mm}:${ss}`;
}

function formatHms(ms) {
  const abs = Math.abs(ms);
  const totalSeconds = Math.floor(abs / 1000);
  const hh = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
  const mm = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
  const ss = String(totalSeconds % 60).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

const DEFAULT_LOCATION = {
  label: 'Sarajevo',
  latitude: 43.8563,
  longitude: 18.4131,
  timezoneOverride: 'Europe/Sarajevo',
};

export function usePrayerTimes({
  locationLabel: initialLocationLabel = '—',
  methodLabel = 'MWL',
  alertsEnabled = true,
} = {}) {
  const { now } = useNow({ intervalMs: 1000 });
  const { request: requestGeo, status: geoStatus } = useGeoLocation();

  const isLoading = ref(false);
  const error = ref(null);
  const locationLabel = ref(initialLocationLabel);

  const prayers = ref([
    { key: 'fajr', time: '--:--' },
    { key: 'dhuhr', time: '--:--' },
    { key: 'asr', time: '--:--' },
    { key: 'maghrib', time: '--:--' },
    { key: 'isha', time: '--:--' },
  ]);

  const selectedCity = ref(null); // { id, name }
  const selectedCoords = ref(null); // { latitude, longitude, timezone? }

  const STORAGE_KEY = 'deenly:praytimes:location';

  function getTimezone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return null;
    }
  }

  function readSavedLocation() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  function saveLocation(payload) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  }

  function formatCoordsLabel(latitude, longitude) {
    const la = Number(latitude);
    const lo = Number(longitude);
    if (!Number.isFinite(la) || !Number.isFinite(lo)) return 'Coordinates';
    return `${la.toFixed(4)}, ${lo.toFixed(4)}`;
  }

  function normalizeCitiesResponse(resp) {
    const list = resp?.data?.data ?? resp?.data ?? [];
    const items = (Array.isArray(list) ? list : [])
      .map((c) => ({
        id: c.id ?? c.city_id ?? c.value,
        name: c.name ?? c.label ?? c.city ?? c.title,
        latitude: c.latitude ?? null,
        longitude: c.longitude ?? null,
        timezone: c.timezone ?? null,
      }))
      .filter((c) => Number.isFinite(Number(c.id)) && typeof c.name === 'string' && c.name.trim().length > 0);

    const nextLink = resp?.data?.meta?.pagination?.links?.next ?? null;
    let nextPage = null;
    if (typeof nextLink === 'string' && nextLink.length > 0) {
      try {
        const url = new URL(nextLink);
        const p = Number(url.searchParams.get('page'));
        if (Number.isFinite(p)) nextPage = p;
      } catch {
        nextPage = null;
      }
    }

    return { items, nextPage };
  }

  async function fetchCities({ name, page = 1 } = {}) {
    const params = {};
    if (Number.isFinite(Number(page)) && Number(page) > 0) params.page = Number(page);
    if (typeof name === 'string' && name.trim().length > 0) params.name = name.trim();

    const resp = await axiosInstance.get('/api/cities', { params });
    return normalizeCitiesResponse(resp);
  }

  async function fetchDayTimes({ cityId, latitude, longitude, timezoneOverride } = {}) {
    const dt = now.value;
    const month = dt.getMonth() + 1;
    const day = dt.getDate();
    const timezone = timezoneOverride || getTimezone();

    const params = { include: 'month' };
    if (timezone) params.timezone = timezone;
    if (Number.isFinite(Number(cityId))) params.city_id = Number(cityId);
    if (typeof latitude === 'number' && typeof longitude === 'number') {
      params.latitude = latitude;
      params.longitude = longitude;
    }

    const resp = await axiosInstance.get(`/api/prayer-times/month/${month}/day/${day}`, { params });
    return resp?.data?.data ?? null;
  }

  function applyTimes(data) {
    if (!data) return;
    const times = {
      fajr: data.fajr,
      dhuhr: data.dhuhr,
      asr: data.asr,
      maghrib: data.maghrib,
      isha: data.isha,
    };
    prayers.value = Object.entries(times).map(([key, time]) => ({
      key,
      time: typeof time === 'string' ? time.slice(0, 5) : '--:--',
    }));
  }

  async function refresh() {
    isLoading.value = true;
    error.value = null;
    try {
      let data = null;
      if (selectedCoords.value) {
        data = await fetchDayTimes({
          latitude: selectedCoords.value.latitude,
          longitude: selectedCoords.value.longitude,
          timezoneOverride: selectedCoords.value.timezone,
        });
      } else if (selectedCity.value?.id) {
        data = await fetchDayTimes({ cityId: selectedCity.value.id });
      } else {
        // No saved location yet: Sarajevo, where most visitors are, until they
        // pick their own. Not saved, so their first choice still sticks.
        locationLabel.value = DEFAULT_LOCATION.label;
        data = await fetchDayTimes(DEFAULT_LOCATION);
      }
      applyTimes(data);
    } catch (e) {
      error.value = e;
    } finally {
      isLoading.value = false;
    }
  }

  async function useMyLocation() {
    const coords = await requestGeo();
    selectedCoords.value = {
      latitude: coords.latitude,
      longitude: coords.longitude,
      timezone: getTimezone(),
      label: 'My location',
    };
    selectedCity.value = null;
    locationLabel.value = 'My location';
    saveLocation({ mode: 'coords', coords: selectedCoords.value });
    await refresh();
  }

  async function setCity(city) {
    selectedCity.value = city;
    selectedCoords.value = null;
    locationLabel.value = city?.name ?? '—';
    saveLocation({ mode: 'city', city: selectedCity.value });
    await refresh();
  }

  async function setCoords({ latitude, longitude, timezone } = {}) {
    const label = formatCoordsLabel(latitude, longitude);
    selectedCoords.value = {
      latitude,
      longitude,
      timezone: timezone || getTimezone(),
      label,
    };
    selectedCity.value = null;
    locationLabel.value = label;
    saveLocation({ mode: 'coords', coords: selectedCoords.value });
    await refresh();
  }

  onMounted(async () => {
    const saved = readSavedLocation();
    if (saved?.mode === 'city' && saved?.city?.id) {
      selectedCity.value = saved.city;
      locationLabel.value = saved.city.name ?? '—';
    }
    if (
      saved?.mode === 'coords' &&
      saved?.coords?.latitude != null &&
      saved?.coords?.longitude != null
    ) {
      selectedCoords.value = saved.coords;
      locationLabel.value =
        saved.coords.label || formatCoordsLabel(saved.coords.latitude, saved.coords.longitude);
    }
    await refresh();
  });

  const prayersWithMinutes = computed(() =>
    prayers.value
      .map((p) => ({ ...p, minutes: parseHmToMinutes(p.time) }))
      .filter((p) => p.minutes != null)
      .sort((a, b) => a.minutes - b.minutes)
  );

  const currentMinutes = computed(() => minutesSinceStartOfDay(now.value));

  const nextPrayerIndex = computed(() => {
    const mins = currentMinutes.value;
    const list = prayersWithMinutes.value;
    if (list.length === 0) return -1;
    const idx = list.findIndex((p) => p.minutes > mins);
    return idx === -1 ? 0 : idx;
  });

  const previousPrayerIndex = computed(() => {
    const nextIdx = nextPrayerIndex.value;
    const list = prayersWithMinutes.value;
    if (list.length === 0 || nextIdx === -1) return -1;
    return nextIdx === 0 ? list.length - 1 : nextIdx - 1;
  });

  const nextPrayer = computed(() => {
    const idx = nextPrayerIndex.value;
    return idx === -1 ? null : prayersWithMinutes.value[idx];
  });

  const previousPrayer = computed(() => {
    const idx = previousPrayerIndex.value;
    return idx === -1 ? null : prayersWithMinutes.value[idx];
  });

  const msToNextPrayer = computed(() => {
    const next = nextPrayer.value;
    if (!next) return 0;
    const nowDate = now.value;
    const todayStart = new Date(nowDate);
    todayStart.setHours(0, 0, 0, 0);

    const nextMs = todayStart.getTime() + next.minutes * 60_000;
    const delta = nextMs - nowDate.getTime();
    if (delta > 0) return delta;

    // next is tomorrow
    return delta + 24 * 60 * 60_000;
  });

  const countdownText = computed(() => formatHms(msToNextPrayer.value));

  const progressRatio = computed(() => {
    const prev = previousPrayer.value;
    const next = nextPrayer.value;
    if (!prev || !next) return 0;

    const mins = currentMinutes.value;
    const prevM = prev.minutes;
    const nextM = next.minutes > prevM ? next.minutes : next.minutes + 24 * 60;
    const curM = mins >= prevM ? mins : mins + 24 * 60;

    const span = nextM - prevM;
    if (span <= 0) return 0;
    return Math.max(0, Math.min(1, (curM - prevM) / span));
  });

  return {
    now,
    prayers: prayersWithMinutes,
    nextPrayer,
    countdownText,
    progressRatio,
    locationLabel,
    methodLabel,
    alertsEnabled,
    isLoading,
    error,
    geoStatus,
    refresh,
    useMyLocation,
    setCity,
    setCoords,
    fetchCities,
  };
}

